import { Upload, X } from "@tamagui/lucide-icons";
import { HubStatus, Hub } from "common";
import { Formik } from "formik";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  Input,
  Label,
  Select,
  YStack,
  Text,
  Spinner,
  Image,
  XStack,
  View,
  ScrollView,
} from "tamagui";

import { useFieldValidationSchema } from "../../hooks/useFieldValidationSchema";
import { iconLabel } from "../../utils/a11y";

interface StationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  station?: Hub | null;
  onSubmit: (values: FormValues) => void;
}
export interface FormValues {
  id?: string;
  name: string;
  serialNumber: string;
  macAddress: string;
  status: string;
  logoUrl?: string;
  logoFile?: File;
}

export function StationFormModal({
  open,
  onOpenChange,
  station,
  onSubmit,
}: StationFormModalProps) {
  const { t } = useTranslation();
  const [loading, setIsLoading] = useState(false);
  const { baseStationFormValidationSchema } = useFieldValidationSchema();
  const mode = station ? "edit" : "create";

  const initialValues: FormValues = station
    ? {
        id: station.id,
        name: station.name,
        serialNumber: station.serialNumber,
        macAddress: station.macAddress,
        status: station.status,
        logoUrl: station.logoUrl,
      }
    : {
        name: "",
        serialNumber: "",
        macAddress: "",
        status: "",
        logoUrl: "",
      };

  const [previewUrl, setPreviewUrl] = useState<string | null>(
    station?.logoUrl || null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    await onSubmit(values);
    onOpenChange(false);
    setIsLoading(false);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFieldValue("logoFile", file);

      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setPreviewUrl(dataUrl);
        setFieldValue("logoUrl", dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearLogoSelection = (
    setFieldValue: (field: string, value: any) => void,
  ) => {
    setFieldValue("logoFile", undefined);
    setFieldValue("logoUrl", "");
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Trigger asChild />
      <Dialog.Portal zIndex={1}>
        <Dialog.Overlay />
        <Dialog.Content
          bordered
          elevate
          size="$8"
          padding="$4"
          width="480px"
          maxWidth="95vw"
          maxHeight="90dvh"
        >
          <Dialog.Title size="$15" fontSize="$6" lineHeight="$6">
            {mode === "create"
              ? t("baseStations.form.createTitle")
              : t("baseStations.form.editTitle")}
          </Dialog.Title>
          <Formik
            initialValues={initialValues}
            validationSchema={baseStationFormValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({
              handleChange,
              handleSubmit,
              values,
              errors,
              setFieldValue,
            }) => (
              // Scrolls once a logo preview makes the form taller than a
              // phone or short laptop screen; the Select menu is portaled.
              <ScrollView
                flexShrink={1}
                marginTop="$2"
                contentContainerStyle={{ gap: "$2" }}
              >
                {mode === "edit" && (
                  <YStack>
                    <Label>{t("baseStations.table.id")}</Label>
                    <Input
                      value={values.id || ""}
                      editable={false}
                      autoFocus={false}
                      focusable={false}
                      tabIndex={-1}
                      backgroundColor="$gray3"
                      color="$gray11"
                      placeholder="Auto-generated"
                    />
                  </YStack>
                )}
                <YStack>
                  <Label>{t("baseStations.form.nameLabel")}</Label>
                  <Input
                    value={values.name}
                    onChangeText={handleChange("name")}
                    placeholder={t("baseStations.form.namePlaceholder")}
                    autoFocus={mode === "edit"}
                  />
                  {errors.name && (
                    <Text fontSize="$2" color="$red10" marginTop="$2">
                      {errors.name}
                    </Text>
                  )}
                </YStack>
                <YStack>
                  <Label>{t("baseStations.form.serialLabel")}</Label>
                  <Input
                    value={values.serialNumber}
                    onChangeText={handleChange("serialNumber")}
                    placeholder={t("baseStations.form.serialPlaceholder")}
                  />
                  {errors.serialNumber && (
                    <Text fontSize="$2" color="$red10" marginTop="$2">
                      {errors.serialNumber}
                    </Text>
                  )}
                </YStack>
                <YStack>
                  <Label>{t("baseStations.form.macAddressLabel")}</Label>
                  <Input
                    value={values.macAddress}
                    onChangeText={handleChange("macAddress")}
                    placeholder={t("baseStations.form.macAddressPlaceholder")}
                  />
                  {errors.macAddress && (
                    <Text fontSize="$2" color="$red10" marginTop="$2">
                      {errors.macAddress}
                    </Text>
                  )}
                </YStack>
                <YStack>
                  <Label>{t("baseStations.form.statusLabel")}</Label>
                  <Select
                    value={values.status}
                    onValueChange={handleChange("status")}
                  >
                    <Select.Trigger>
                      <Select.Value
                        placeholder={t("baseStations.form.statusPlaceholder")}
                        fontSize="$4"
                      />
                    </Select.Trigger>
                    <Select.Content>
                      <Select.ScrollUpButton />
                      <Select.Viewport>
                        <Select.Item value={HubStatus.Active} index={0}>
                          <Select.ItemText>
                            {t("baseStations.form.statusOptions.active")}
                          </Select.ItemText>
                        </Select.Item>
                        <Select.Item value={HubStatus.Inactive} index={1}>
                          <Select.ItemText>
                            {t("baseStations.form.statusOptions.inactive")}
                          </Select.ItemText>
                        </Select.Item>
                        <Select.Item value={HubStatus.Maintenance} index={1}>
                          <Select.ItemText>
                            {t("baseStations.form.statusOptions.maintenance")}
                          </Select.ItemText>
                        </Select.Item>
                        <Select.Item value={HubStatus.Pending} index={0}>
                          <Select.ItemText>
                            {t("baseStations.form.statusOptions.pending")}
                          </Select.ItemText>
                        </Select.Item>
                      </Select.Viewport>
                    </Select.Content>
                  </Select>
                  {errors.status && (
                    <Text fontSize="$2" color="$red10" marginTop="$2">
                      {errors.status}
                    </Text>
                  )}
                </YStack>

                <YStack>
                  <Label>Logo</Label>
                  <YStack space="$2" marginTop="$2">
                    {previewUrl && (
                      <XStack alignItems="center" marginBottom="$2">
                        <View
                          width={100}
                          height={100}
                          borderRadius="$2"
                          overflow="hidden"
                          borderWidth={1}
                          borderColor="$borderColor"
                          marginRight="$2"
                        >
                          <Image
                            source={{ uri: previewUrl }}
                            width="100%"
                            height="100%"
                            resizeMode="contain"
                          />
                        </View>
                        <Button
                          icon={<X size="$1" />}
                          onPress={() => clearLogoSelection(setFieldValue)}
                          {...iconLabel(t("a11y.removeLogo"))}
                          size="$2"
                        />
                      </XStack>
                    )}

                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleFileChange(e, setFieldValue)}
                      ref={fileInputRef}
                    />

                    <Button
                      onPress={() => fileInputRef.current?.click()}
                      icon={<Upload size="$1" />}
                      theme="gray"
                    >
                      {previewUrl ? "Change Logo" : "Upload Logo"}
                    </Button>
                  </YStack>
                </YStack>
                <YStack
                  space="$2"
                  marginTop="$4"
                  flexDirection="row"
                  justifyContent="flex-end"
                >
                  <Button
                    onPress={() => onOpenChange(false)}
                    color={"$color12"}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onPress={() => handleSubmit()}
                    backgroundColor={"$hgPrimary"}
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner color="white" size="small" />
                    ) : (
                      <Text>{t("common.submit")}</Text>
                    )}
                  </Button>
                </YStack>
              </ScrollView>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

export default StationFormModal;
